import { useMemo, useCallback, useEffect } from 'react';
import { Row } from "./Flexbox";
import Text from './Text';
import JobtrackBarItem from './JobtrackBarItem';

const JobtrackBar = (props) => {
    const {selectedBar, onClick} = props;

    const hexToRgba = (hex, alpha = 1) => {
        hex = hex.replace(/^#/, '');
        if(hex.length === 3) {
            hex = hex.split('').map(c => c + c).join('');
        }

        const r = parseInt(hex.slice(0, 2), 16);
        const g = parseInt(hex.slice(2, 4), 16);
        const b = parseInt(hex.slice(4, 6), 16);

        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    };

    const toTitleCase = (str) => {
        if(!str || typeof str !== 'string') {
            return '';
        }

        return str.toLowerCase().split(' ').map(word => {
            if(word.length === 0) {
                return '';
            }
            return word.charAt(0).toUpperCase() + word.slice(1);
        }).join(' ');
    }

    const baseColor = useCallback((percentage) => {
        if (percentage >= 80 && percentage <= 100) {
            return hexToRgba(props.theme.Lime);
        } else if (percentage >= 0 && percentage < 80) {
            return hexToRgba(props.theme.Orange);
        }
        return 'rgba(0,0,0,0)'; 
    }, [props.theme]);

    const chartData = useMemo(() => {
        if (!props.data || props.data.length === 0 || !props.selection) {
            return { bars: [], generalData: {}, maxGlobalPercentage: 0};
        }

        const aggregateData = {};
        const generalData = {};
        let maxPercentage = 0;

        props.data.forEach(item => {
            let key, value;

            //Set the key according to the resource
            if (props.selection.resource === 'NomeOperador') {
                key = toTitleCase(item.NomeOperador);
                // if(key === 'Metrics') { //For some reason, this is an "operator". Apparently it's used for testing... If needed, uncomment this to hide it
                //     return;
                // }
            } else if (props.selection.resource === 'CodRecurso') {
                key = item.CodRecurso;
            } else {
                return;
            }

            //Set value according to the type, ommit zeroes in the graph
            if (props.selection.type === 'EfArreglo') {
                value = item.EfArreglo;
                if (value === 0) {
                    return; 
                }
            } else if (props.selection.type === 'Eficiencia') {
                value = item.Eficiencia;
                if (value === 0) {
                    return; 
                }
            } else if (props.selection.type === 'EfTotal') {
                value = item.EfTotal;
                if (value === 0) {
                    return; 
                }
            } else {
                return;
            }

            if (!aggregateData[key]) {
                aggregateData[key] = { sum: 0, count: 0 };
                generalData[key] = [];
            }

            aggregateData[key].sum += (typeof value === 'number' ? value : 0); //Sum values if they're numbers
            aggregateData[key].count += 1;

            const formattedPercentage = parseFloat(value).toFixed(2);
            if(props.selection.resource === "NomeOperador") {
                generalData[key].push({ //Necessary to show the components of each bar
                    NumOrdem: item.NumOrdem,
                    NomeOperador: toTitleCase(item.NomeOperador),
                    CodRecurso: item.CodRecurso,
                    Fecha: item.FormatDate,
                    SecondCol: item.SecondCol,
                    percentage: formattedPercentage
                });
            } else {    //Aggregates data for operators (SecondCol has the operators when resource === CodRecurso)
                if(!generalData[key].SecondCol) {
                    generalData[key].SecondCol = {};
                }

                const operator = toTitleCase(item.SecondCol);
                if(!generalData[key].SecondCol[operator]) {
                    generalData[key].SecondCol[operator] = {sum: 0, count: 0};
                }
                generalData[key].SecondCol[operator].sum += value;
                generalData[key].SecondCol[operator].count += 1;
            }
            
        });

        const processedBars = [];

        for (const key in aggregateData) {
            let average = Math.round(aggregateData[key].sum / aggregateData[key].count);

            if (Math.abs(average) > maxPercentage) {
                maxPercentage = Math.abs(average);
            }

            let barColor, barOverflowColor;
            if (average < 0) {
                barColor = hexToRgba(props.theme.Red);
                barOverflowColor = 'transparent';
            } else if (average > 100) {
                barColor = hexToRgba(props.theme.Lime);
                barOverflowColor = hexToRgba(props.theme.Yellow);
            } else {
                barColor = baseColor(average);
                barOverflowColor = 'transparent';
            }

            processedBars.push({
                label: key,
                mainValue: average < 0 ? Math.min(Math.abs(average), 100) : (average > 100 ? 100 : average), //Math.min caps the bar size at 100, this is required for negative values.
                overflowValue: average > 100 ? average - 100 : 0,
                color: barColor,
                overflowColor: barOverflowColor,
                originalValue: average,
            });

            if(props.selection.resource === "CodRecurso") {
                const operators = [];

                for (const op in generalData[key].SecondCol) {
                    const opAverage = Math.round(generalData[key].SecondCol[op].sum / generalData[key].SecondCol[op].count);    //Calculate de individual average for each operator

                    operators.push({
                        SecondCol: op,
                        percentage: opAverage.toFixed(2),
                    });
                }
                generalData[key] = operators;   //Assign the data to generalKey (this is passed to BarItem)
            }
        }

        processedBars.sort((a, b) => b.originalValue - a.originalValue);    //Sort by higher value

        const indexedBars = processedBars.map((bar, index) => ({ ...bar, index }));
        const maxGlobalPercentage = maxPercentage > 100 ? maxPercentage : 100;

        return {
            processedBars: indexedBars,
            generalData: generalData,
            maxGlobalPercentage: maxGlobalPercentage,
        };
    }, [props.data, props.selection, props.theme, baseColor]);

    const handleBarClick = (index) => {
        onClick(prevIndex => (prevIndex === index ? null : index));
    };

    useEffect(() => {
        props.onDataReady?.(chartData.processedBars);
    }, [chartData, props.onDataReady]);

    return (
        <Row style={{ width: '100%', flexDirection: 'column', overflowY: 'auto', minHeight: 0, maxHeight: '100%', alignItems: 'stretch', justifyContent: 'flex-start' }}>
            {chartData.processedBars.length > 0 ? (
                chartData.processedBars.map(bar => (
                    <JobtrackBarItem key={bar.label} barData={bar} details={chartData.generalData[bar.label]} selection={props.selection} theme={props.theme} isSelected={selectedBar === bar.index} onClick={handleBarClick} maxGlobalPercentage={chartData.maxGlobalPercentage}/>
                ))
            ) : (
                <Text color={props.theme.ONED} style={{ textAlign: 'center', padding: '20px' }}>
                    No hay datos disponibles para mostrar.
                </Text>
            )}
        </Row>
    );
};

export default JobtrackBar;